import React, { useState, useEffect } from 'react';
import API from '../../utils/api';
import StudentProfileModal from './StudentProfileModal';

function FeesView({ branchId }) {
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Collect Fee Modal State
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMode: 'Cash',
    paymentDate: new Date().toISOString().split('T')[0],
    referenceNumber: '',
    remarks: ''
  });

  // History & Receipt State
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  // Profile State
  const [showProfileModal, setShowProfileModal] = useState(false);

  useEffect(() => {
    fetchFees();
  }, [branchId]);

  const fetchFees = async () => {
    try {
      // Using the students endpoint since it joins fee data
      const url = branchId ? `/api/admin/students?branchId=${branchId}` : '/api/admin/students';
      const res = await API.get(url);
      setFees(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch fees:', error);
      setLoading(false);
    }
  };

  const handleCollectClick = (student) => {
    setSelectedStudent(student);
    setPaymentData({
      amount: '',
      paymentMode: 'Cash',
      paymentDate: new Date().toISOString().split('T')[0],
      referenceNumber: '',
      remarks: ''
    });
    setShowCollectModal(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        studentId: selectedStudent._id,
        amount: paymentData.amount,
        paymentMode: paymentData.paymentMode,
        paymentDate: paymentData.paymentDate,
        referenceNumber: paymentData.referenceNumber,
        remarks: paymentData.remarks,
        receivedBy: 'Admin'
      };
      const res = await API.post('/api/admin/payments/collect', payload);
      if (res.data.success) {
        alert('Payment collected successfully! Receipt generated.');
        setShowCollectModal(false);
        fetchFees(); // Refresh table
        
        // Auto-open receipt
        handleViewReceipt(res.data.payment._id);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to collect payment');
    }
  };

  const handleViewHistory = async (student) => {
    setSelectedStudent(student);
    try {
      const res = await API.get(`/api/admin/payments/history/${student._id}`);
      setPaymentHistory(res.data);
      setShowHistoryModal(true);
    } catch (error) {
      alert('Failed to fetch payment history');
    }
  };

  const handleViewReceipt = async (paymentId) => {
    try {
      const res = await API.get(`/api/admin/payments/receipt/${paymentId}`);
      if (res.data.success) {
        setReceiptData(res.data);
        setShowReceiptModal(true);
      }
    } catch (error) {
      alert('Failed to fetch receipt');
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const filteredFees = fees.filter(fee => 
    fee.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    fee.course?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fee.branch?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-view">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
        <h2>Manage Fees</h2>
        <input 
          type="text" 
          placeholder="Search by student name or course..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #ccc', minWidth: '250px' }}
        />
      </div>

      <div className="table-box">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Student</th>
                <th>Course</th>
                <th>Total Fee</th>
                <th>Paid</th>
                <th>Pending</th>
                <th>Next Due</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{textAlign: "center"}}>Loading fee records...</td>
                </tr>
              ) : filteredFees.length > 0 ? (
                filteredFees.map((record) => {
                  const feeAmount = record.feeAmount || 0;
                  const paidAmount = record.paidAmount || 0;
                  const pendingAmount = feeAmount - paidAmount;
                  
                  return (
                    <tr key={record._id}>
                      <td style={{ fontWeight: '600' }}>{record.name}</td>
                      <td>
                        {record.course}
                      </td>
                      <td style={{ color: '#0b4f6c', fontWeight: '500' }}>₹{feeAmount.toLocaleString('en-IN')}</td>
                      <td style={{ color: '#7AC943', fontWeight: '600' }}>₹{paidAmount.toLocaleString('en-IN')}</td>
                      <td style={{ color: pendingAmount > 0 ? '#ef4444' : '#64748b', fontWeight: '600' }}>
                        ₹{pendingAmount > 0 ? pendingAmount.toLocaleString('en-IN') : 0}
                      </td>
                      <td>{record.nextDueDate ? new Date(record.nextDueDate).toLocaleDateString('en-GB') : 'N/A'}</td>
                      <td>
                        <span className={
                          record.status === 'Completed' ? 'paid' : 
                          record.status === 'Partially Paid' ? 'partially-paid' : 'pending'
                        }>
                          {record.status === 'Completed' ? 'Completed' : record.status}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          <button onClick={() => { setSelectedStudent(record); setShowProfileModal(true); }} style={{ padding: '5px 8px', backgroundColor: '#64748b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Profile</button>
                          <button onClick={() => handleCollectClick(record)} style={{ padding: '5px 8px', backgroundColor: '#7AC943', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Collect Fee</button>
                          <button onClick={() => handleViewHistory(record)} style={{ padding: '5px 8px', backgroundColor: '#0ea5e9', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>History</button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan="6" style={{textAlign: "center"}}>No fee records found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Collect Fee Modal */}
      {showCollectModal && selectedStudent && (
        <div className="modal-overlay">
          <div className="modal-content">
            <span className="close-btn" onClick={() => setShowCollectModal(false)}>&times;</span>
            <h3 style={{ borderBottom: '2px solid #0b4f6c', paddingBottom: '10px', marginBottom: '20px', color: '#0b4f6c' }}>COLLECT FEE</h3>
            
            <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '5px', fontSize: '14px' }}>
              <p><strong>Student:</strong> {selectedStudent.name} ({selectedStudent.course})</p>
              <p><strong>Pending Amount:</strong> ₹{Math.max(0, (selectedStudent.feeAmount || 0) - (selectedStudent.paidAmount || 0)).toLocaleString('en-IN')}</p>
            </div>

            <form onSubmit={handlePaymentSubmit} className="client-form">
              <div className="input-group">
                <label>Payment Date *</label>
                <input type="date" required value={paymentData.paymentDate} onChange={(e) => setPaymentData({...paymentData, paymentDate: e.target.value})} />
              </div>

              <div className="input-group">
                <label>Amount Paid (₹) *</label>
                <input type="number" required max={Math.max(0, (selectedStudent.feeAmount || 0) - (selectedStudent.paidAmount || 0))} value={paymentData.amount} onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})} />
              </div>

              <div className="input-group">
                <label>Payment Mode *</label>
                <select required value={paymentData.paymentMode} onChange={(e) => setPaymentData({...paymentData, paymentMode: e.target.value})}>
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank">Bank Transfer</option>
                  <option value="Card">Card</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>

              {(paymentData.paymentMode !== 'Cash') && (
                <div className="input-group">
                  <label>Reference Number</label>
                  <input type="text" placeholder="Transaction ID, Cheque No, etc." value={paymentData.referenceNumber} onChange={(e) => setPaymentData({...paymentData, referenceNumber: e.target.value})} />
                </div>
              )}

              <div className="input-group">
                <label>Remarks</label>
                <input type="text" value={paymentData.remarks} onChange={(e) => setPaymentData({...paymentData, remarks: e.target.value})} />
              </div>

              <div className="submit-area" style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button type="submit" className="submit-btn" style={{ flex: 1, backgroundColor: '#7AC943' }}>Collect Payment</button>
                <button type="button" className="submit-btn" onClick={() => setShowCollectModal(false)} style={{ flex: 1, backgroundColor: '#94a3b8' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showHistoryModal && selectedStudent && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <span className="close-btn" onClick={() => setShowHistoryModal(false)}>&times;</span>
            <h3 style={{ borderBottom: '2px solid #0b4f6c', paddingBottom: '10px', marginBottom: '20px', color: '#0b4f6c' }}>PAYMENT HISTORY</h3>
            
            <h4 style={{ marginBottom: '15px' }}>{selectedStudent.name} ({selectedStudent.course})</h4>

            <table className="edu-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Receipt No</th>
                  <th>Amount</th>
                  <th>Mode</th>
                  <th>Ref/Remarks</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.length > 0 ? paymentHistory.map(payment => (
                  <tr key={payment._id}>
                    <td>{new Date(payment.paymentDate).toLocaleDateString('en-GB')}</td>
                    <td>{payment.receiptNumber}</td>
                    <td style={{ color: '#7AC943', fontWeight: 'bold' }}>₹{payment.amount.toLocaleString('en-IN')}</td>
                    <td>{payment.paymentMode}</td>
                    <td>{payment.referenceNumber || payment.remarks || 'N/A'}</td>
                    <td>
                      <button onClick={() => handleViewReceipt(payment._id)} style={{ padding: '4px 8px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Receipt</button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center' }}>No payment history found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Printable Receipt Modal */}
      {showReceiptModal && receiptData && (
        <div className="modal-overlay printable-receipt-overlay">
          <div className="modal-content receipt-modal-content" style={{ maxWidth: '800px', width: '100%' }}>
            <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '20px' }}>
              <button onClick={handlePrintReceipt} className="submit-btn" style={{ backgroundColor: '#10b981' }}>Print Receipt</button>
              <button onClick={() => setShowReceiptModal(false)} className="submit-btn" style={{ backgroundColor: '#ef4444' }}>Close</button>
            </div>

            <div className="printable-receipt-area" style={{ padding: '40px', border: '1px solid #ccc', backgroundColor: '#fff', color: '#000' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #000', paddingBottom: '20px', marginBottom: '20px' }}>
                <div>
                  <h1 style={{ margin: '0 0 10px 0', color: '#0b4f6c' }}>{receiptData.settings?.instituteName || 'WEBMOK PVT. LTD.'}</h1>
                  <p style={{ margin: '0 0 5px 0' }}>{receiptData.settings?.instituteAddress}</p>
                  <p style={{ margin: '0 0 5px 0' }}>Phone: {receiptData.settings?.phone || '8684031003'} | Email: {receiptData.settings?.email || 'info@webmok.in'}</p>
                  {receiptData.settings?.gstNumber && <p style={{ margin: 0 }}>GST: {receiptData.settings.gstNumber}</p>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <h2 style={{ margin: '0 0 10px 0', color: '#555' }}>FEE RECEIPT</h2>
                  <p style={{ margin: '0 0 5px 0' }}><strong>Receipt No:</strong> {receiptData.receipt.receiptNumber}</p>
                  <p style={{ margin: '0 0 5px 0' }}><strong>Date:</strong> {new Date(receiptData.payment.paymentDate).toLocaleDateString('en-GB')}</p>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: '0 0 8px 0' }}><strong>Student Name:</strong> {receiptData.payment.studentId.name}</p>
                  <p style={{ margin: '0 0 8px 0' }}><strong>Course:</strong> {receiptData.payment.studentId.course}</p>
                  <p style={{ margin: '0 0 8px 0' }}><strong>Phone:</strong> {receiptData.payment.studentId.phone || 'N/A'}</p>
                </div>
                <div style={{ flex: 1, textAlign: 'right' }}>
                  <p style={{ margin: '0 0 8px 0' }}><strong>Total Course Fee:</strong> ₹{(receiptData.payment.studentId.totalFee || 0).toLocaleString('en-IN')}</p>
                  <p style={{ margin: '0 0 8px 0' }}><strong>Total Pending Balance:</strong> ₹{(receiptData.payment.studentId.pendingFee || 0).toLocaleString('en-IN')}</p>
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f1f5f9' }}>
                    <th style={{ padding: '12px', border: '1px solid #cbd5e1', textAlign: 'left' }}>Description</th>
                    <th style={{ padding: '12px', border: '1px solid #cbd5e1', textAlign: 'center' }}>Payment Mode</th>
                    <th style={{ padding: '12px', border: '1px solid #cbd5e1', textAlign: 'center' }}>Ref / Remarks</th>
                    <th style={{ padding: '12px', border: '1px solid #cbd5e1', textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '12px', border: '1px solid #cbd5e1' }}>Fee Payment</td>
                    <td style={{ padding: '12px', border: '1px solid #cbd5e1', textAlign: 'center' }}>{receiptData.payment.paymentMode}</td>
                    <td style={{ padding: '12px', border: '1px solid #cbd5e1', textAlign: 'center' }}>{receiptData.payment.referenceNumber || receiptData.payment.remarks || '-'}</td>
                    <td style={{ padding: '12px', border: '1px solid #cbd5e1', textAlign: 'right', fontWeight: 'bold' }}>₹{receiptData.payment.amount.toLocaleString('en-IN')}</td>
                  </tr>
                </tbody>
              </table>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '60px' }}>
                <div>
                  <p style={{ fontStyle: 'italic', color: '#666', margin: 0 }}>{receiptData.settings.footerMessage || 'Thank you for your payment.'}</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ borderBottom: '1px solid #000', width: '200px', marginBottom: '5px', height: '40px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                    {receiptData.settings.authorizedSignature ? (
                      <span style={{ fontFamily: 'cursive', fontSize: '20px' }}>{receiptData.settings.authorizedSignature}</span>
                    ) : null}
                  </div>
                  <p style={{ margin: 0 }}>Authorized Signature</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Student Profile Modal */}
      {showProfileModal && selectedStudent && (
        <StudentProfileModal student={selectedStudent} onClose={() => setShowProfileModal(false)} />
      )}
    </div>
  );
}

export default FeesView;
